import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { FormNavigator } from '../shared/FormNavigator';
import { RouteName } from '../types';

import type { Step } from '@/components/Stepper/StepItem';

import { useGetCycleEvaluation } from '@/api/cycle-evaluation-services/cycle-evaluation';
import {
    defineAudienceOnNextAtom,
    lastSavedAtAtom,
    questionnaireCreationOnNextAtom,
    scheduleCreationOnNextAtom,
    selectEvaluatorsOnNextAtom,
    selectTemplateOnNextAtom,
} from '@/atoms/cycleEvaluation';
import {
    CycleEvaluationStatus,
    EvaluationType,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import {
    CycleEvaluationSteps,
    type CycleEvaluationStepsType,
} from '@/constants/cycle-evaluation/CycleEvaluationSteps';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { DefineAudiencePage } from '@/pages/CycleEvaluation/DefineAudience/DefineAudiencePage';
import { CycleEvaluationQuestionnairePage } from '@/pages/CycleEvaluation/Questionnaire/CycleEvaluationQuestionnairePage';
import { ScheduleCreationPage } from '@/pages/CycleEvaluation/ScheduleCreation/ScheduleCreationPage';
import { SelectEvaluatorsPage } from '@/pages/CycleEvaluation/SelectEvaluators/SelectEvaluatorsPage';
import { SelectTemplatePage } from '@/pages/CycleEvaluation/SelectTemplate/SelectTemplatePage';
import {
    getCycleEvaluationInitialMaxStep,
    getCycleEvaluationStepFromPath,
    getCycleEvaluationStepPath,
} from '@/utils/cycleEvaluation';

export interface CycleEvaluationFormNavigatorParams {
    step: string;
    cycleEvaluationId: string;
}
const ROUTES = [
    {
        path: `${RouteName.CycleEvaluationForm}/${getCycleEvaluationStepPath(CycleEvaluationSteps.DEFINE_AUDIENCE)}/:cycleEvaluationId`,
        component: DefineAudiencePage,
    },
    {
        path: `${RouteName.CycleEvaluationForm}/${getCycleEvaluationStepPath(CycleEvaluationSteps.SELECT_EVALUATORS)}/:cycleEvaluationId`,
        component: SelectEvaluatorsPage,
    },
    {
        path: `${RouteName.CycleEvaluationForm}/${getCycleEvaluationStepPath(CycleEvaluationSteps.SELECT_TEMPLATE)}/:cycleEvaluationId`,
        component: SelectTemplatePage,
    },
    {
        path: `${RouteName.CycleEvaluationForm}/${getCycleEvaluationStepPath(CycleEvaluationSteps.QUESTIONNAIRE_CREATION)}/:cycleEvaluationId`,
        component: CycleEvaluationQuestionnairePage,
    },
    {
        path: `${RouteName.CycleEvaluationForm}/${getCycleEvaluationStepPath(CycleEvaluationSteps.SCHEDULE_CREATION)}/:cycleEvaluationId`,
        component: ScheduleCreationPage,
    },
];

export const CycleEvaluationFormNavigator = () => {
    const navigate = useNavigate();
    const { t } = useAmelioTranslations();
    const { step, cycleEvaluationId } = useParams<CycleEvaluationFormNavigatorParams>();

    const { data: cycleEvaluation } = useGetCycleEvaluation(cycleEvaluationId);

    const isSelfEvaluation = cycleEvaluation?.reviewType === EvaluationType.SELF_EVALUATION;
    const isManagerEvaluation = cycleEvaluation?.reviewType === EvaluationType.MANAGER_EVALUATION;
    const shouldSkipEvaluatorsStep = isSelfEvaluation || isManagerEvaluation;

    const templateStepBypassedSteps: CycleEvaluationStepsType[] = [
        CycleEvaluationSteps.QUESTIONNAIRE_CREATION,
        CycleEvaluationSteps.SCHEDULE_CREATION,
    ];
    const isTemplateStepCompleted =
        !!cycleEvaluation?.cycleQuestionnaire?.reviewTemplateId ||
        templateStepBypassedSteps.includes(
            cycleEvaluation?.currentStep as CycleEvaluationStepsType,
        );

    const defineAudienceOnNext = useAtomValue(defineAudienceOnNextAtom);
    const selectEvaluatorsOnNext = useAtomValue(selectEvaluatorsOnNextAtom);
    const selectTemplateOnNext = useAtomValue(selectTemplateOnNextAtom);
    const questionnaireCreationOnNext = useAtomValue(questionnaireCreationOnNextAtom);
    const scheduleCreationOnNext = useAtomValue(scheduleCreationOnNextAtom);
    const lastSavedAt = useAtomValue(lastSavedAtAtom);

    const allSteps: Step[] = [
        {
            id: CycleEvaluationSteps.DEFINE_AUDIENCE,
            label: t('cycleEvaluationStepper.defineAudience.stepTitle'),
        },
        {
            id: CycleEvaluationSteps.SELECT_EVALUATORS,
            label: t('cycleEvaluationStepper.selectEvaluators.stepTitle'),
        },
        {
            id: CycleEvaluationSteps.SELECT_TEMPLATE,
            label: t('cycleEvaluationStepper.templateSelection.stepTitle'),
        },
        {
            id: CycleEvaluationSteps.QUESTIONNAIRE_CREATION,
            label: t('cycleEvaluationStepper.questionnaireCreation.stepTitle'),
        },
        {
            id: CycleEvaluationSteps.SCHEDULE_CREATION,
            label: t('cycleEvaluationStepper.scheduleCreation.stepTitle'),
        },
    ];

    // Keep the step while the user is on it (via "Change Template"); filtering it out then breaks FormNavigator's step lookup.
    const isOnSelectTemplateStep =
        getCycleEvaluationStepFromPath(step) === CycleEvaluationSteps.SELECT_TEMPLATE;

    const steps = allSteps.filter((s) => {
        if (shouldSkipEvaluatorsStep && s.id === CycleEvaluationSteps.SELECT_EVALUATORS) {
            return false;
        }
        if (
            isTemplateStepCompleted &&
            !isOnSelectTemplateStep &&
            s.id === CycleEvaluationSteps.SELECT_TEMPLATE
        ) {
            return false;
        }
        return true;
    });

    const routes = ROUTES.filter((r) => {
        if (
            shouldSkipEvaluatorsStep &&
            r.path.includes(
                getCycleEvaluationStepPath(CycleEvaluationSteps.SELECT_EVALUATORS) ?? '',
            )
        ) {
            return false;
        }
        if (
            isTemplateStepCompleted &&
            !isOnSelectTemplateStep &&
            r.path.includes(getCycleEvaluationStepPath(CycleEvaluationSteps.SELECT_TEMPLATE) ?? '')
        ) {
            return false;
        }
        return true;
    });

    const onNextCallbacks = useMemo(
        () => ({
            [CycleEvaluationSteps.DEFINE_AUDIENCE]: defineAudienceOnNext,
            [CycleEvaluationSteps.SELECT_EVALUATORS]: selectEvaluatorsOnNext,
            [CycleEvaluationSteps.SELECT_TEMPLATE]: selectTemplateOnNext,
            [CycleEvaluationSteps.QUESTIONNAIRE_CREATION]: questionnaireCreationOnNext,
            [CycleEvaluationSteps.SCHEDULE_CREATION]: scheduleCreationOnNext,
        }),
        [
            defineAudienceOnNext,
            selectEvaluatorsOnNext,
            selectTemplateOnNext,
            questionnaireCreationOnNext,
            scheduleCreationOnNext,
        ],
    );

    const onSuccess = () => {
        navigate({ path: RouteName.CycleEvaluationSuccessMessage });
    };

    const initialMaxStep = getCycleEvaluationInitialMaxStep({ cycleEvaluation, steps });
    const isLaunched = cycleEvaluation?.status === CycleEvaluationStatus.ACTIVE;

    return (
        <FormNavigator
            steps={steps}
            routePrefix={RouteName.CycleEvaluationForm}
            urlParamName={'cycleEvaluationId'}
            getStepFromPath={getCycleEvaluationStepFromPath}
            getStepPath={getCycleEvaluationStepPath}
            backRoute={RouteName.CycleEvaluation}
            onSuccess={onSuccess}
            onNextCallbacks={onNextCallbacks}
            lastSavedAt={lastSavedAt}
            stepperTitle={t('cycleEvaluationStepper.title')}
            stepperSubtitle={t('cycleEvaluationStepper.subtitle')}
            stepperDataTestId={'cycle-evaluation-stepper'}
            routes={routes}
            initialMaxStep={initialMaxStep}
            isLaunched={isLaunched}
        />
    );
};
